import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SpeakingBigIcon } from '../../../assets';
import { ArrowLeft, Mic, X } from 'lucide-react';
import api from '../../../utils/api';

const SpeakingPreviewPage = () => {
  const navigate = useNavigate();
  const [showMicModal, setShowMicModal] = useState(false);
  const [volume, setVolume] = useState(0);
  const [micError, setMicError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [attemptsError, setAttemptsError] = useState('');
  const [sessionLoading, setSessionLoading] = useState(false);

  useEffect(() => {
    const loadRemainingAttempts = async () => {
      setAttemptsLoading(true);
      setAttemptsError('');
      try {
        const { data } = await api.get('/speaking/attempts/remaining');
        if (data?.success) {
          setRemainingAttempts(data.data?.remaining ?? 0);
        }
      } catch (err) {
        console.error("Failed to load speaking attempts:", err);
        setAttemptsError(err.response?.data?.message || "Gagal memeriksa kuota.");
        setRemainingAttempts(null);
      } finally {
        setAttemptsLoading(false);
      }
    };

    loadRemainingAttempts();
  }, []);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (showMicModal) {
      startMicTest();
    } else {
      stopMicTest();
    }
    return () => stopMicTest();
  }, [showMicModal]);

  const startMicTest = async () => {
    setMicError('');
    setVolume(0);

    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError("Browser tidak mendukung akses mikrofon. Pastikan halaman dibuka melalui HTTPS.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      audioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      analyserRef.current = analyser;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;
      
      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;
      
      const updateVolume = () => {
        analyser.getByteTimeDomainData(dataArray);
        let sumSquares = 0;

        for (let i = 0; i < bufferLength; i++) {
          const normalized = (dataArray[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }

        const rms = Math.sqrt(sumSquares / bufferLength);
        const volumePercentage = Math.min(100, Math.round(rms * 320));
        setVolume(volumePercentage);
        animationFrameRef.current = requestAnimationFrame(updateVolume);
      };
      
      updateVolume();
    } catch (err) {
      console.error("Mic access error:", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicError("Akses mikrofon diblokir. Izinkan akses mikrofon dari pengaturan browser.");
        return;
      }

      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setMicError("Mikrofon tidak ditemukan. Pastikan perangkat mikrofon aktif.");
        return;
      }

      setMicError("Akses mikrofon gagal. Pastikan halaman dibuka melalui HTTPS dan mikrofon tersedia.");
    }
  };

  const stopMicTest = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    dataArrayRef.current = null;
    sourceRef.current = null;
    setVolume(0);
  };

  const handleBackToIELTSTest = () => {
    navigate('/IELTSTest'); // Arahkan ke halaman utama IELTS Test
  };

  const handleStartTest = async () => {
    try {
      setSessionLoading(true);
      const response = await api.post('/speaking/sessions');
      if (response.data?.success) {
        const { session_id, questions, instructions } = response.data.data;
        navigate('/speaking/soal', { state: { sessionId: session_id, questions, instructions } });
      }
    } catch (err) {
      console.error("Failed to start session:", err);
      const errMsg = err.response?.data?.message || "Gagal memulai sesi baru. Silakan coba lagi.";
      alert(errMsg);
    } finally {
      setSessionLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#E0E7FF] to-white py-6 px-4 sm:py-8 md:py-12 lg:py-16 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto">
        {/* Card Utama */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl lg:shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[400px] md:min-h-[450px] lg:min-h-[500px]">
          
          {/* === BAGIAN KIRI: Informasi Tes === */}
          <div className="bg-gradient-to-r from-[#1e3e7e] to-[#a51c30] p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12 flex flex-col justify-between text-white w-full md:w-1/2 overflow-y-auto">
            <div className="space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl font-bold font-poppins">
                INFORMASI TES
              </h2>

              {/* List Informasi */}
              <ul className="text-[10px] xs:text-xs sm:text-xs md:text-sm lg:text-sm font-roboto space-y-1 list-disc list-inside leading-tight">
                <li>Terdiri dari 3 Bagian (Part 1, Part 2, Part 3)</li>
                <li>Durasi: 11 - 14 Menit</li>
                <li>Kamu memerlukan Mikrofon yang berfungsi dengan baik</li>
                <li>Usahakan berada di tempat yang tenang agar suara terekam dengan jelas</li>
              </ul>

              <h3 className="text-base sm:text-lg md:text-xl lg:text-xl xl:text-2xl font-semibold font-poppins mt-3 sm:mt-4">
                PANDUAN MENGERJAKAN:
              </h3>
              <ul className="text-[10px] xs:text-xs sm:text-xs md:text-sm lg:text-sm font-roboto space-y-1 list-disc list-inside leading-tight">
                <li>Tekan tombol rekam untuk mulai menjawab setiap pertanyaan</li>
                <li>Part 2 memberikan waktu persiapan 1 menit sebelum mulai berbicara selama 2 menit</li>
                <li>Berbicaralah dengan jelas dan natural</li>
                <li>Setelah selesai, jawaban akan dievaluasi oleh mentor</li>
              </ul>

              <div className="bg-red-600 text-white p-2.5 sm:p-3 md:p-4 lg:p-5 xl:p-6 rounded-lg sm:rounded-xl text-[10px] xs:text-xs sm:text-xs md:text-sm lg:text-sm font-roboto mt-4 leading-tight">
                <p className="font-bold mb-1">PERHATIAN!</p>
                <p>
                  Setelah kamu menekan tombol <strong>"Mulai Test"</strong>,
                  pastikan kamu memberikan izin (allow) pada browser untuk menggunakan <strong>Mikrofon</strong>.
                </p>
              </div>

              {remainingAttempts === 0 && (
                <div className="bg-amber-600 text-white p-2.5 sm:p-3 md:p-4 lg:p-5 xl:p-6 rounded-lg sm:rounded-xl text-[10px] xs:text-xs sm:text-xs md:text-sm lg:text-sm font-roboto mt-4 leading-tight">
                  <p className="font-bold mb-1">BATAS PERCOBAAN TERCAPAI!</p>
                  <p>
                    Kamu sudah mencapai batas 2 kali percobaan untuk minggu ini. Kuota percobaan baru akan di-reset pada hari Senin depan.
                  </p>
                </div>
              )}
            </div>

            {/* Tombol Kembali ke IELTS Test & Test Mic */}
            <div className="flex items-center gap-3 sm:gap-4 mt-4 sm:mt-6 md:mt-8 lg:mt-10">
              <button
                onClick={handleBackToIELTSTest}
                className="bg-transparent border-2 border-white text-white font-poppins font-semibold text-xs sm:text-sm md:text-base lg:text-lg px-4 sm:px-5 md:px-6 lg:px-7 py-2 sm:py-2.5 md:py-3 rounded-full hover:bg-white hover:text-[#1e3e7e] transition flex items-center gap-1.5 sm:gap-2 w-fit"
              >
                <ArrowLeft size={16} className="sm:size-5 md:size-6 lg:size-6" />
                Kembali
              </button>
              <button
                onClick={() => setShowMicModal(true)}
                className="bg-white text-[#1e3e7e] border-2 border-white font-poppins font-semibold text-xs sm:text-sm md:text-base lg:text-lg px-4 sm:px-5 md:px-6 lg:px-7 py-2 sm:py-2.5 md:py-3 rounded-full hover:bg-gray-100 transition flex items-center gap-1.5 sm:gap-2 w-fit shadow-md"
              >
                <Mic size={16} className="sm:size-5 md:size-6 lg:size-6" />
                Test Mic
              </button>
            </div>
          </div>

          {/* === BAGIAN KANAN: Preview Icon === */}
          <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12 flex flex-col justify-between text-center w-full md:w-1/2">
            
            {/* Judul + Icon + Pesan */}
            <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8 flex-1 flex flex-col justify-center items-center">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-4xl font-bold text-[#1e3e7e] font-poppins">
                SPEAKING TEST
              </h1>

              {/* Icon Headphone */}
              <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 bg-none flex items-center justify-center p-3 sm:p-4 md:p-5 lg:p-6">
                <img
                  src={SpeakingBigIcon}
                  alt="Speaking Icon"
                  className="w-full h-full object-contain"
                />
              </div>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-gray-700 font-roboto max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg leading-relaxed">
                Persiapkan dirimu dan mikrofon sebelum memulai simulasi test ini.
              </p>
            </div>

            {/* Tombol Mulai Test */}
            <div className="flex flex-col items-end gap-2 mt-4 sm:mt-6 md:mt-8 lg:mt-10">
              {attemptsLoading ? (
                <span className="text-xs text-gray-500 font-poppins">Memeriksa kuota...</span>
              ) : remainingAttempts !== null ? (
                <span className="text-xs text-gray-600 font-poppins">
                  Sisa kuota minggu ini: <strong className="text-[#1e3e7e]">{remainingAttempts}</strong>
                </span>
              ) : attemptsError ? (
                <span className="text-xs text-red-500 font-poppins">{attemptsError}</span>
              ) : null}

              <button
                onClick={handleStartTest}
                disabled={attemptsLoading || remainingAttempts === 0 || sessionLoading}
                className={`font-poppins font-bold text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl px-5 sm:px-6 md:px-7 lg:px-8 py-2 sm:py-2.5 md:py-3 lg:py-3.5 rounded-full shadow-md transition ${
                  attemptsLoading || remainingAttempts === 0 || sessionLoading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-yellow-400 text-[#1e3e7e] hover:bg-yellow-500"
                }`}
              >
                {sessionLoading ? "Memulai..." : "Mulai Test"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mic Test Modal */}
      {showMicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-sm w-full shadow-2xl relative transform transition-all">
            <button 
              onClick={() => setShowMicModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
            >
              <X size={24} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-[#1e3e7e]">
                <Mic size={32} />
              </div>
              <h3 className="font-anton text-2xl text-[#1e3e7e] mb-2">Test Mikrofon</h3>
              <p className="font-poppins text-sm text-gray-600 leading-relaxed">
                Bicaralah ke mikrofon Anda untuk memastikan audio tertangkap dengan baik.
              </p>
            </div>

            {micError ? (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-center font-poppins text-sm mb-4">
                {micError}
              </div>
            ) : (
              <div className="bg-gray-100 rounded-full h-6 w-full overflow-hidden mb-6 relative shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-75 ease-out"
                  style={{ width: `${volume}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 mix-blend-overlay">
                  {volume}%
                </div>
              </div>
            )}

            <button
              onClick={() => setShowMicModal(false)}
              className="w-full py-3 bg-[#1e3e7e] text-white rounded-xl font-poppins font-semibold hover:bg-blue-800 transition shadow-md"
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default SpeakingPreviewPage;
